import type { KeyboardEvent } from 'react'

import {
  IconButton,
  IconSearch,
  InputGroup,
  Select,
  type SelectValue,
  SubText,
  TextField,
} from '@procurify/ui'
import { noop } from 'lodash'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { DataManagerSearchStyled } from '../../styles/DataManagerSearchStyled'

export interface IDataManagerSearchProps {
  value: string
  category?: SelectValue
  categoryOptions?: SelectValue[]
  showManualSearchText?: boolean
  onChange?(searchTerm: string): void
  onSearch?(searchTerm: string): void
  onCategoryChange?(category: SelectValue): void
  autoFocus?: boolean
  placeholder?: string
}

export const DataManagerSearch = ({
  value = '',
  category = null,
  categoryOptions = [],
  showManualSearchText = false,
  onChange = noop,
  onCategoryChange = noop,
  onSearch = noop,
  autoFocus = false,
  placeholder,
}: IDataManagerSearchProps) => {
  const intl = useProcurifyIntl()

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch(value)
    }
  }

  return (
    <DataManagerSearchStyled>
      <InputGroup>
        {categoryOptions && categoryOptions.length > 0 && (
          <Select
            styles={{
              menu: (base) => ({
                ...base,
                width: 'max-content',
                minWidth: '100%',
              }),
            }}
            isSearchable={false}
            value={category}
            placeholder={intl.formatMessage(messages.categorySelectPlaceholder)}
            options={categoryOptions}
            onChange={(newCategory) => onCategoryChange(newCategory)}
          />
        )}
        <TextField
          type='search'
          placeholder={
            placeholder || intl.formatMessage(messages.searchPlaceholder)
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
        />
        <InputGroup.Item>
          <IconButton
            iconComponent={<IconSearch />}
            onClick={() => onSearch(value)}
            aria-label={intl.formatMessage(messages.updateSearchButtonLabel)}
          />
        </InputGroup.Item>
      </InputGroup>

      {showManualSearchText && (
        <SubText className='manual-trigger-text'>
          {intl.formatMessage(messages.manualSearchPrompt)}
        </SubText>
      )}
    </DataManagerSearchStyled>
  )
}

DataManagerSearch.displayName = 'ControlledDataManagerSearch'
